import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardBody, CardHeader, Button, Spinner, Dropdown, DropdownTrigger, DropdownMenu, DropdownItem, Tabs, Tab, Table, TableHeader, TableColumn, TableBody, TableRow, TableCell, Chip } from '@heroui/react';
import { Icon } from '@iconify/react';
import { motion } from 'framer-motion';
import CountUp from 'react-countup';
import { 
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';

import { useDashboardData } from './hooks/use-dashboard-data';
import { WorldMap } from './components/world-map';
import { StatCard } from './components/stat-card';
import { ContentStatCard } from './components/content-stat-card';
import { LoadingSpinner } from '../../components/loading-spinner';
import { ErrorState } from '../../components/error-state';
import type { TimePeriod } from '../../types/analytics';
import { analyticsService } from '../../services/analytics.service';
import { addToast } from '@heroui/react';

const Dashboard: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [timeRange, setTimeRange] = React.useState<TimePeriod>('week');
  const [selectedTab, setSelectedTab] = React.useState('overview');
  const [isExporting, setIsExporting] = React.useState(false);
  
  const { 
    loading, 
    error,
    overallStats, 
    visitStats, 
    contentStats, 
    topCountries, 
    topLanguages, 
    topPages, 
    aiChatStats, 
    faqStats,
    refetch
  } = useDashboardData(timeRange);
  
  const timeRangeOptions: Array<{ key: TimePeriod; text: string }> = [
    { key: 'today', text: t('today') },
    { key: 'week', text: t('thisWeek') },
    { key: 'month', text: t('thisMonth') },
    { key: 'year', text: t('thisYear') }
  ];

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };
  
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

  const handleExportReport = async () => {
    try {
      setIsExporting(true);
      
      await analyticsService.exportDashboardReport(
        { period: timeRange },
        i18n.language
      );
      
      addToast({
        title: t('success'),
        description: t('reportExportedSuccessfully') || 'Report exported successfully',
        severity: 'success',
      });
    } catch (error) {
      console.error('Export error:', error);
      addToast({
        title: t('error'),
        description: t('failedToExportReport') || 'Failed to export report',
        severity: 'error',
      });
    } finally {
      setIsExporting(false);
    }
  };

  if (loading) {
    return <LoadingSpinner variant="page" />;
  }

  if (error) {
    return (
      <ErrorState
        variant="page"
        message={t('errorLoadingData')}
        onRetry={() => refetch && refetch()}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold">{t('dashboard')}</h1>
        <div className="flex items-center gap-3">
          <Dropdown>
            <DropdownTrigger>
              <Button 
                variant="flat" 
                endContent={<Icon icon="lucide:chevron-down" />}
              >
                {timeRangeOptions.find(option => option.key === timeRange)?.text}
              </Button>
            </DropdownTrigger>
            <DropdownMenu 
              aria-label="Time range options"
              onAction={(key) => setTimeRange(key as TimePeriod)}
              selectedKeys={[timeRange]}
              selectionMode="single"
            >
              {timeRangeOptions.map((option) => (
                <DropdownItem key={option.key}>{option.text}</DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>
          
          <Button 
            color="primary" 
            startContent={<Icon icon="lucide:download" />}
            onPress={handleExportReport}
            isLoading={isExporting}
            isDisabled={isExporting}
          >
            {isExporting ? t('exporting') || 'Exporting...' : t('exportReport')}
          </Button>
        </div>
      </div>
      
      <motion.div 
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
        variants={container}
        initial="hidden"
        animate="show"
      >
        <StatCard 
          title={t('totalVisits')}
          value={overallStats.totalVisits}
          icon="lucide:users"
          color="bg-primary-100 text-primary-600"
          change={overallStats.visitsChange}
          changeType={overallStats.visitsChange >= 0 ? 'positive' : 'negative'}
        />
        
        <StatCard 
          title={t('activeUsers')}
          value={overallStats.activeUsers}
          icon="lucide:user-check"
          color="bg-green-100 text-green-600"
          change={overallStats.activeUsersChange}
          changeType={overallStats.activeUsersChange >= 0 ? 'positive' : 'negative'}
        />
        
        <StatCard 
          title={t('averageSessionTime')}
          value={overallStats.avgSessionTime}
          icon="lucide:clock"
          color="bg-purple-100 text-purple-600"
          change={overallStats.avgSessionTimeChange}
          changeType={overallStats.avgSessionTimeChange >= 0 ? 'positive' : 'negative'}
          isTime={true}
        />
        
        <StatCard 
          title={t('bounceRate')}
          value={overallStats.bounceRate}
          icon="lucide:arrow-left-right"
          color="bg-amber-100 text-amber-600"
          change={overallStats.bounceRateChange}
          changeType={overallStats.bounceRateChange <= 0 ? 'positive' : 'negative'}
          isPercentage={true}
        />
      </motion.div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">{t('visitsOverTime')}</h2>
            <Tabs 
              size="sm" 
              aria-label="Chart options"
              selectedKey={selectedTab}
              onSelectionChange={setSelectedTab as any}
            >
              <Tab key="overview" title={t('overview')} />
              <Tab key="detailed" title={t('detailed')} />
            </Tabs>
          </CardHeader>
          <CardBody>
            <div className="h-80">
              {visitStats.overview.length === 0 || (visitStats.overview.length === 1 && visitStats.overview[0]?.visits === 0) ? (
                <div className="flex flex-col items-center justify-center h-full text-default-400">
                  <Icon icon="lucide:bar-chart-2" width={64} className="mb-4 opacity-50" />
                  <p className="text-lg font-medium">{t('noDataAvailable')}</p>
                  <p className="text-sm mt-2">{t('visitsDataWillAppearHere') || 'Data will appear here when available'}</p>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  {selectedTab === 'overview' ? (
                  <AreaChart
                    data={visitStats.overview}
                    margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient id="colorVisits" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#8884d8" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="date" />
                    <YAxis />
                    <CartesianGrid strokeDasharray="3 3" />
                    <Tooltip />
                    <Area 
                      type="monotone" 
                      dataKey="visits" 
                      stroke="#8884d8" 
                      fillOpacity={1} 
                      fill="url(#colorVisits)" 
                    />
                  </AreaChart>
                ) : (
                  <LineChart
                    data={visitStats.detailed}
                    margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="desktop" 
                      stroke="#8884d8" 
                      activeDot={{ r: 8 }} 
                    />
                    <Line type="monotone" dataKey="mobile" stroke="#82ca9d" />
                    <Line type="monotone" dataKey="tablet" stroke="#ffc658" />
                  </LineChart>
                  )}
                </ResponsiveContainer>
              )}
            </div>
          </CardBody>
        </Card>
        
        <Card>
          <CardHeader>
            <h2 className="text-lg font-semibold">{t('topCountries')}</h2>
          </CardHeader>
          <CardBody>
            <div className="h-80 flex flex-col">
              <div className="flex-1 mb-4">
                <WorldMap data={topCountries} />
              </div>
              <div className="space-y-2">
                {topCountries.slice(0, 3).map((country, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{country.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-2 bg-default-100 dark:bg-default-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-primary rounded-full" 
                          style={{ width: `${(country.visits / topCountries[0].visits) * 100}%` }}
                        />
                      </div>
                      <span className="text-sm text-default-500">{country.visits.toLocaleString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </CardBody>
        </Card>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <h2 className="text-lg font-semibold">{t('topLanguages')}</h2>
          </CardHeader>
          <CardBody>
            <div className="h-64">
              {topLanguages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-default-400">
                  <Icon icon="lucide:languages" width={64} className="mb-4 opacity-50" />
                  <p className="text-lg font-medium">{t('noDataAvailable')}</p>
                  <p className="text-sm mt-2">{t('languageDataWillAppearHere') || 'Language data will appear here'}</p>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                  <Pie
                    data={topLanguages}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {topLanguages.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [`${value} ${t('visits')}`, t('visits')]} />
                </PieChart>
              </ResponsiveContainer>
              )}
            </div>
          </CardBody>
        </Card>
        
        <Card>
          <CardHeader>
            <h2 className="text-lg font-semibold">{t('mostViewedPages')}</h2>
          </CardHeader>
          <CardBody>
            <div className="h-64 overflow-auto">
              {topPages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-default-400">
                  <Icon icon="lucide:file-text" width={64} className="mb-4 opacity-50" />
                  <p className="text-lg font-medium">{t('noDataAvailable')}</p>
                  <p className="text-sm mt-2">{t('pageDataWillAppearHere') || 'Page data will appear here'}</p>
                </div>
              ) : (
                <Table 
                removeWrapper 
                aria-label="Most viewed pages"
                hideHeader
              >
                <TableHeader>
                  <TableColumn>{t('page')}</TableColumn>
                  <TableColumn>{t('views')}</TableColumn>
                </TableHeader>
                <TableBody>
                  {topPages.map((page, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-default-100 flex items-center justify-center">
                            <span className="text-xs font-medium">{index + 1}</span>
                          </div>
                          <div className="flex flex-col">
                            <span className="text-sm font-medium truncate max-w-[200px]">{page.title}</span>
                            <span className="text-xs text-default-400 truncate max-w-[200px]">{page.path}</span>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end">
                          <Chip size="sm" variant="flat">{page.views.toLocaleString()}</Chip>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              )}
            </div>
          </CardBody>
        </Card>
        
        <Card>
          <CardHeader>
            <h2 className="text-lg font-semibold">{t('aiChatInteractions')}</h2>
          </CardHeader>
          <CardBody>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={aiChatStats}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="questions" fill="#8884d8" />
                  <Bar dataKey="responses" fill="#82ca9d" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardBody>
        </Card>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <ContentStatCard 
          title={t('hadiths')}
          count={contentStats.hadiths.count}
          change={contentStats.hadiths.change}
          icon="lucide:book-open"
          color="bg-emerald-100 text-emerald-600"
        />
        
        <ContentStatCard 
          title={t('books')}
          count={contentStats.books.count}
          change={contentStats.books.change}
          icon="lucide:book"
          color="bg-primary-100 text-primary-600"
        />
        
        <ContentStatCard 
          title={t('magazines')}
          count={contentStats.magazines.count}
          change={contentStats.magazines.change}
          icon="lucide:newspaper"
          color="bg-purple-100 text-purple-600"
        />
        
        <ContentStatCard 
          title={t('wikiArticles')}
          count={contentStats.wikiArticles.count}
          change={contentStats.wikiArticles.change}
          icon="lucide:file-text"
          color="bg-amber-100 text-amber-600"
        />
        
        <ContentStatCard 
          title={t('galleryItems')}
          count={contentStats.galleryItems.count}
          change={contentStats.galleryItems.change}
          icon="lucide:image"
          color="bg-rose-100 text-rose-600"
        />
      </div>
      
      <Card>
        <CardHeader>
          <h2 className="text-lg font-semibold">{t('frequentlyAskedQuestions')}</h2>
        </CardHeader>
        <CardBody>
          <Table 
            removeWrapper 
            aria-label="Frequently asked questions"
          >
            <TableHeader>
              <TableColumn>{t('question')}</TableColumn>
              <TableColumn>{t('category')}</TableColumn>
              <TableColumn>{t('language')}</TableColumn>
              <TableColumn>{t('frequency')}</TableColumn>
              <TableColumn>{t('lastAsked')}</TableColumn>
            </TableHeader>
            <TableBody>
              {faqStats.map((faq, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="font-medium">{faq.question}</span>
                      <span className="text-xs text-default-400 truncate max-w-[300px]">{faq.answer}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Chip size="sm" variant="flat" color="primary">{faq.category}</Chip>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="text-sm">{faq.language}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="w-20 h-2 bg-default-100 dark:bg-default-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-primary rounded-full" 
                          style={{ width: `${(faq.frequency / faqStats[0].frequency) * 100}%` }}
                        />
                      </div>
                      <span className="text-sm">{faq.frequency}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">{faq.lastAsked}</span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardBody>
      </Card>
    </div>
  );
};

export default Dashboard;
